<template>
  <transition
    id="dev-PointDetailsDialog"
    name="slide-up"
  >
    <div
      v-if="point"
      class="point-dialog"
    >
      <div class="point-dialog-content">
        <ModalCloseButton
          class="dialog-close-btn"
          @close="handleClose"
        />

        <!-- Header with Name, Address, and Distance -->
        <div class="dialog-header">
          <div class="header-main">
            <h3 class="point-name">
              {{ point.name }}
            </h3>
            <div class="address-row-mobile">
              <a
                :href="googleMapsUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="point-address point-address-link"
              >
                <font-awesome-icon
                  icon="fa-solid fa-location-arrow"
                  class="address-icon"
                />
                {{ point.address }}
                <font-awesome-icon
                  icon="fa-solid fa-external-link-alt"
                  class="external-icon"
                />
              </a>
              <!-- Distance Badge - Shown next to address on mobile -->
              <div
                v-if="distanceFromUser"
                class="distance-badge distance-badge-mobile"
                :class="{ 'distance-far': isUserFarFromPoint }"
              >
                <font-awesome-icon
                  icon="fa-solid fa-location-dot"
                  class="badge-icon"
                />
                <span class="distance-value">a {{ distanceFromUser }} km</span>
              </div>
            </div>
          </div>
          <!-- Distance Badge - Original position for desktop -->
          <div
            v-if="distanceFromUser"
            class="distance-badge distance-badge-desktop"
            :class="{ 'distance-far': isUserFarFromPoint }"
          >
            <font-awesome-icon
              icon="fa-solid fa-location-dot"
              class="badge-icon"
            />
            <span class="distance-value">a {{ distanceFromUser }} km</span>
          </div>
        </div>

        <!-- Availability Card -->
        <AvailabilityCard
          :available-batteries="point.available_batteries"
          :available-slots="point.available_slots"
        />

        <!-- Token Card -->
        <TokenInputCard
          v-model="editedToken"
          :readonly="false"
          :autofocus="false"
        />

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center">
          <div
            v-if="point && isOffline"
            class="no-battery-message offline-message"
          >
            <font-awesome-icon icon="fa-solid fa-plug-circle-xmark" />
            <span>{{ $t('map_navigation.terminal_offline') }}</span>
          </div>
          <template v-else-if="isReturnMode">
            <div
              v-if="hasAvailability"
              class="return-mode-message"
            >
              <font-awesome-icon icon="fa-solid fa-hand-holding-heart" />
              <span>{{ $t('map_navigation.return_here') }}</span>
            </div>
            <div
              v-else
              class="no-battery-message"
            >
              <font-awesome-icon icon="fa-solid fa-battery-empty" />
              <span>{{ noAvailabilityMessage }}</span>
            </div>
          </template>
          <template v-else-if="isLoggedIn">
            <BaseButton
              v-if="hasAvailability"
              variant="primary"
              unstyled
              class="select-station-btn"
              :disabled="!canWithdraw"
              :aria-label="actionButtonText"
              @click="startCheckoutFlow"
            >
              {{ actionButtonText }}
            </BaseButton>
            <div
              v-else
              class="no-battery-message"
            >
              <font-awesome-icon icon="fa-solid fa-battery-empty" />
              <span>{{ noAvailabilityMessage }}</span>
            </div>
          </template>
          <template v-else>
            <div class="unauthenticated-container">
              <p class="login-prompt">
                {{ $t('login.fast_login') }}
              </p>
              <BaseButton
                variant="secondary"
                unstyled
                :aria-label="$t('login.sign_in_or_create')"
                @click="$router.push('/login-social')"
              >
                {{ $t('login.sign_in_or_create') }}
              </BaseButton>
            </div>
          </template>
        </div>

        <!-- Check Prices Link -->
        <div class="check-prices-container">
          <BaseButton
            bare
            variant=""
            class="check-prices-link"
            @click="$emit('check-prices')"
          >
            {{ $t('map_navigation.check_prices') }}
          </BaseButton>
        </div>
      </div>

      <!-- Distance Confirmation Modal -->
      <DistanceConfirmationModal
        :show="showDistanceModal"
        :distance="distanceFromUser"
        :token="point.token"
        @confirm="proceedToCheckout"
        @cancel="showDistanceModal = false"
      />
    </div>
  </transition>
</template>

<script>
  import AvailabilityCard from '../ui/AvailabilityCard.vue'
  import TokenInputCard from '../ui/TokenInputCard.vue'
  import DistanceConfirmationModal from '../modals/DistanceConfirmationModal.vue'
  import { calculateDistance, isPointOffline } from '../utils/mapHelpers'
  import { MAX_DISTANCE_KM } from '../utils/constants'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'

  export default {
    name: 'PointDetailsDialog',
    components: {
      AvailabilityCard,
      TokenInputCard,
      DistanceConfirmationModal,
      BaseButton,
      ModalCloseButton
    },
    props: {
      mode: {
        type: String,
        default: 'withdrawal',
        validator: value => ['withdrawal', 'return'].includes(value)
      },
      point: {
        type: Object,
        default: null
      },
      userLocation: {
        type: Object,
        default: null
      },
      hasLocationPermission: {
        type: Boolean,
        default: false
      },
      user: {
        type: Object,
        default: () => ({})
      }
    },
    emits: ['close', 'check-prices', 'start-checkout', 'token-changed'],
    data() {
      return {
        showDistanceModal: false,
        editedToken: this.point?.token || ''
      }
    },
    computed: {
      isReturnMode() {
        return this.mode === 'return'
      },

      isOffline() {
        return isPointOffline(this.point)
      },

      isLoggedIn() {
        return !!localStorage.getItem('token')
      },

      distanceFromUser() {
        if (!this.point || !this.userLocation || !this.hasLocationPermission) {
          return null
        }
        const distance = calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          parseFloat(this.point.geolocation.lat),
          parseFloat(this.point.geolocation.lng)
        )
        return distance.toFixed(1)
      },

      isUserFarFromPoint() {
        if (!this.distanceFromUser) {
          return false
        }
        return parseFloat(this.distanceFromUser) > MAX_DISTANCE_KM
      },

      canWithdraw() {
        if (!this.point || this.isOffline) return false

        const slots = parseInt(this.point.available_slots || this.point.avaible_slots || 0)
        const batteries = parseInt(this.point.available_batteries || this.point.avaible_batteries || 0)

        if (this.isReturnMode) {
          return slots > 0 && this.point.token && this.point.token.trim() !== ''
        }
        return batteries > 0 && this.point.token && this.point.token.trim() !== ''
      },

      hasAvailability() {
        if (this.isOffline) return false
        const slots = parseInt(this.point?.available_slots || this.point?.avaible_slots || 0)
        const batteries = parseInt(this.point?.available_batteries || this.point?.avaible_batteries || 0)

        if (this.isReturnMode) {
          return slots > 0
        }
        return batteries > 0
      },

      actionButtonText() {
        return this.isReturnMode ? this.$t('map_navigation.return_here') : this.$t('map_navigation.select_station')
      },

      noAvailabilityMessage() {
        return this.isReturnMode ? this.$t('map_navigation.no_slots') : this.$t('map_navigation.no_batteries')
      },

      googleMapsUrl() {
        if (!this.point || !this.point.geolocation) {
          return '#'
        }
        const lat = this.point.geolocation.lat
        const lng = this.point.geolocation.lng
        return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      }
    },
    watch: {
      point: {
        immediate: false,
        handler(newPoint) {
          if (newPoint) {
            this.editedToken = newPoint.token || ''
          }
        }
      },
      editedToken(newToken) {
        if (newToken && newToken.length === 4 && newToken !== (this.point?.token || '')) {
          this.$emit('token-changed', newToken)
        }
      }
    },
    methods: {
      startCheckoutFlow() {
        if (this.isUserFarFromPoint) {
          this.showDistanceModal = true
        } else {
          this.proceedToCheckout()
        }
      },

      proceedToCheckout() {
        this.showDistanceModal = false
        const pointWithEditedToken = { ...this.point, token: this.editedToken }
        this.$emit('start-checkout', pointWithEditedToken)
      },

      handleClose() {
        this.$emit('close')
      }
    }
  }
</script>

<style scoped lang="scss">
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }

  .point-dialog {
    position: absolute;
    bottom: var(--app-banner-bottom-height, 0);
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    z-index: 1100;
    max-height: 85vh;
    overflow-y: auto;
  }

  .point-dialog-content {
    padding: 24px;
    position: relative;
  }

  .dialog-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
  }

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 20px;
    padding-right: 40px; /* Space for close button */
  }

  .header-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0; /* Important for flex children truncation */
  }

  .address-row-mobile {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
  }

  .point-name {
    font-size: 16px;
    font-weight: 600;
    color: #424242;
    margin: 0;
    line-height: 1.3;
  }

  .point-address {
    font-size: 12px;
    color: #666;
    margin: 0;
    line-height: 1.4;
    flex: 1;
    min-width: 0;

    &.point-address-link {
      display: inline-flex;
      align-items: flex-start;
      gap: 5px;
      text-decoration: none;
      color: #1976d2;
      transition: all 0.25s ease;
      padding: 4px 0;
      border-radius: 6px;
      font-weight: 500;
      word-break: break-word;

      .address-icon {
        margin-top: 3px;
      }

      &:hover {
        color: #1565c0;
        background: rgba(25, 118, 210, 0.08);
        text-decoration: none;
        transform: translateX(2px);
      }

      &:active {
        transform: translateX(2px) scale(0.98);
      }

      .address-icon {
        font-size: 11px;
        color: #1976d2;
        opacity: 0.85;
      }

      .external-icon {
        font-size: 9px;
        opacity: 0.6;
        margin-left: 1px;
      }
    }
  }

  .distance-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    background: rgba(25, 118, 210, 0.08);
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    color: #1976d2;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.25s ease;
    border: 1px solid rgba(25, 118, 210, 0.15);

    &.distance-badge-mobile {
      display: none;
      padding: 4px 8px;
      font-size: 11px;
    }

    &.distance-far {
      background: rgba(255, 107, 53, 0.1);
      color: #f4511e;
      border: 1px solid rgba(255, 107, 53, 0.3);
      animation: pulse-warning 1.5s ease-in-out infinite;
    }
  }

  @keyframes pulse-warning {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }

  .badge-icon {
    font-size: 14px;
  }

  .distance-value {
    font-weight: var(--fw-semibold);
  }

  .select-station-btn {
    padding: 10px 24px;
    background: #ff6600;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 0 auto;
    display: block;

    &:hover:not(:disabled) {
      background: #e55c00;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 102, 0, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      background: #bdbdbd;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .no-battery-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    background: #ffebee;
    color: #c62828;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    width: 100%;
  }

  .return-mode-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    width: 100%;
  }

  .unauthenticated-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .login-prompt {
    font-size: 13px;
    color: #666;
    margin: 0;
    text-align: center;
    font-weight: 500;
  }

  .check-prices-container {
    text-align: center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
  }

  .check-prices-link {
    padding: 4px 0;
    background: transparent;
    color: #5f82a5;
    border: none;
    border-bottom: 1px solid #0066cc;
    border-radius: 0;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    margin: 0;

    &:hover {
      color: #0052a3;
      border-bottom-color: #0052a3;
    }

    &:active {
      opacity: 0.8;
    }
  }

  @media (max-width: 768px) {
    .point-dialog-content {
      padding: 20px;
    }

    .dialog-header {
      margin-bottom: 16px;
      padding-right: 36px; /* Space for close button */
    }

    .address-row-mobile {
      display: flex !important;
      align-items: center;
    }

    .distance-badge-desktop {
      display: none !important;
    }

    .distance-badge-mobile {
      display: flex !important;
      margin-left: auto;
      padding: 3px 8px;
      font-size: var(--fs-xs);
      height: fit-content;
      align-self: center;
    }

    .point-name {
      font-size: var(--fs-base);
    }
  }

  @media (max-width: 480px) {
    .point-dialog-content {
      padding: 16px;
    }

    .dialog-header {
      padding-right: 32px;
    }

    .point-name {
      font-size: var(--fs-sm);
    }

    .point-address {
      font-size: var(--fs-xs);
    }

    .distance-badge-mobile {
      padding: 3px 8px;
      font-size: var(--fs-xs);
    }

    .select-station-btn {
      padding: 10px 16px;
      font-size: var(--fs-xs);
    }
  }
</style>
